import { ArrowRight, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Container } from '../../components/ui/Container';
import { PageHero } from '../../components/ui/PageHero';
import { pb } from '../../lib/pb';

/**
 * Branded landing page for PocketBase's email-verification confirmation link.
 *
 * PB's email template embeds {APP_URL}/auth/confirm-verification/{TOKEN}
 * (overridden from the default /_/#/... Studio URL). We render here, POST the
 * token to PB's confirm endpoint, then show a clear success or error state.
 */
export function ConfirmVerification(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<'pending' | 'success' | 'error'>('pending');
  const [errorText, setErrorText] = useState<string>('');

  useEffect(() => {
    const previous = document.title;
    document.title = 'Confirm verification | Sunset Harbor Condominiums';
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorText('No verification token in the link.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await pb.collection('users').confirmVerification(token);
        if (!cancelled) setState('success');
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setState('error');
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'That link is invalid or has expired. Ask the board to resend it.';
        setErrorText(message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <>
      <PageHero
        eyebrow="Account"
        title="Confirm verification"
        lead="Finishing the email verification for your Sunset Harbor account."
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mx-auto max-w-md rounded-2xl border border-brand-surface bg-brand-white p-8 shadow-card md:p-10">
            {state === 'pending' ? (
              <div className="flex items-center gap-3 text-brand-body-dark">
                <Loader2 size={20} className="animate-spin text-brand-yellow-deep" aria-hidden="true" />
                <p className="text-sm">Verifying your email...</p>
              </div>
            ) : null}

            {state === 'success' ? (
              <div>
                <div className="flex items-start gap-3">
                  <span
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal"
                    aria-hidden="true"
                  >
                    <CheckCircle2 size={22} strokeWidth={2} />
                  </span>
                  <div>
                    <h2 className="text-xl font-bold text-brand-charcoal">Email verified</h2>
                    <p className="mt-2 text-sm leading-relaxed text-brand-body-dark">
                      Your email has been confirmed with the association. You can now sign in.
                    </p>
                  </div>
                </div>
                <Link
                  to="/login"
                  className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-brand-yellow px-5 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-yellow-deep"
                >
                  Continue to sign-in
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            ) : null}

            {state === 'error' ? (
              <div>
                <div className="flex items-start gap-3">
                  <span
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-red-100 text-red-700"
                    aria-hidden="true"
                  >
                    <XCircle size={22} strokeWidth={2} />
                  </span>
                  <div>
                    <h2 className="text-xl font-bold text-brand-charcoal">We could not verify</h2>
                    <p className="mt-2 text-sm leading-relaxed text-brand-body-dark">{errorText}</p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md border border-brand-surface bg-brand-white px-5 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-surface"
                >
                  Contact the board
                </Link>
              </div>
            ) : null}
          </div>
        </Container>
      </section>
    </>
  );
}
